import type { CSSProperties, FC } from 'react'
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react'
import type { TransitionStatus } from 'react-transition-group'

import type { PopoverProps } from './types'
import useSize from '../../../hooks/use-size'
import useTimeout from '../../../hooks/use-timeout'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import { __DEV__, isNotEmptyObject } from '../../../utils/assertion'
import debounce from '../../../utils/debounce'
import { EMPTY_OBJECT } from '../../../utils/object'
import ownerDocument from '../../../utils/owner-document'
import ownerWindow from '../../../utils/owner-window'
import type { PaneProps } from '../../_layouts'
import { PanePrivate } from '../../_layouts/pane/pane'
import type { TransitionProps as TransitionPropsType } from '../_types/props'
import type { ClickAwayListenerProps } from '../click-away-listener'
import ClickAwayListener from '../click-away-listener/click-away-listener'
import Grow from '../grow/grow'
import Modal from '../modal/modal'
import type { ModalProps } from '../modal/types'
// import useWhyDidYouUpdate from '../../../hooks/use-why-did-you-update'

export function getOffsetTop(
  rect: DOMRect | string,
  vertical: number | string | undefined
): number {
  let offset = 0

  if (typeof vertical === 'number') {
    offset = vertical
  } else if (typeof rect === 'object') {
    if (vertical === 'center') {
      offset = rect.height / 2
    } else if (vertical === 'bottom') {
      offset = rect.height
    }
  }

  return offset
}

export function getOffsetLeft(
  rect: DOMRect | string,
  horizontal: number | string | undefined
): number {
  let offset = 0

  if (typeof horizontal === 'number') {
    offset = horizontal
  } else if (typeof rect === 'object') {
    if (horizontal === 'center') {
      offset = rect.width / 2
    } else if (horizontal === 'right') {
      offset = rect.width
    }
  }

  return offset
}

export const getPopoverAndAnchorStatus = (
  popoverElement: HTMLElement | null | undefined,
  anchorElement: HTMLElement | null | undefined
) => {
  let ol: boolean | undefined
  let it: boolean | undefined
  let ib: boolean | undefined

  if (popoverElement && anchorElement) {
    const pt = Math.round(popoverElement?.getBoundingClientRect().top ?? 0)
    const pb = Math.round(
      popoverElement?.getBoundingClientRect().top +
        popoverElement?.offsetHeight ?? 0
    )
    const at = Math.round(anchorElement?.getBoundingClientRect().top ?? 0)
    const ab = Math.round(
      anchorElement?.getBoundingClientRect().top +
        anchorElement?.offsetHeight ?? 0
    )

    ol = (pt < at + 1 && pb > at + 1) || (pt < ab - 1 && pb > ab - 1)

    // the +/- 1 is because even though the top and bottom pixels of each element line
    // up in the UI, for some reason, the popover is one pixel off.
    it = at + 1 - pb === 0
    ib = ab - 1 - pt === 0
  } else {
    ol = undefined
    it = undefined
    ib = undefined
  }

  return {
    isPopoverOverlappingAnchor: ol,
    isPopoverOpeningUp: it,
    isPopoverOpeningDown: ib
  }
}

function getTransformOriginValue(transformOrigin: {
  horizontal: number | string
  vertical: number | string
}) {
  return [transformOrigin.horizontal, transformOrigin.vertical]
    .map(n => (typeof n === 'number' ? `${n}px` : n))
    .join(' ')
}

// Sum the scrollTop between two elements.
function getScrollParent(parent: Element, child: Element) {
  let element = child
  let scrollTop = 0

  while (element && element !== parent) {
    element = element.parentElement as Element
    scrollTop += element.scrollTop
  }
  return scrollTop
}

function getAnchorEl(
  anchorEl?: Element | ((element?: Element) => Element) | null
) {
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl
}

const defaultOffset = {
  x: 0,
  y: 0
}

const backdropProps: ModalProps['BackdropProps'] = { invisible: true }

const Popover: FC<PopoverProps> = React.forwardRef<
  HTMLDivElement,
  PopoverProps
>(function Popover(props, ref) {
  // eslint-disable-next-line no-console
  // console.log(` ---- popover ----`)
  // useWhyDidYouUpdate('Popover', props)
  const {
    action,
    anchorEl,
    anchorOrigin = {
      vertical: 'top',
      horizontal: 'left'
    },
    anchorPosition,
    anchorReference = 'anchorEl',
    children,
    container: containerProp,
    getContentAnchorEl,
    marginThreshold = 12, //Temp = 12 |  default = 16
    onEnter,
    onEntered,
    onEntering,
    onExit,
    onExited,
    onExiting,
    onReposition,
    open,
    paneProps = EMPTY_OBJECT as PaneProps &
      Pick<PrivateThemeUiProps, '__themeKey' | '__css'>,
    transformOrigin = {
      vertical: 'top',
      horizontal: 'left'
    },
    TransitionComponent = Grow,
    transitionDuration: transitionDurationProp = 'auto',
    TransitionProps = EMPTY_OBJECT as TransitionPropsType,
    transitionStyles = EMPTY_OBJECT as Partial<
      Record<TransitionStatus, CSSProperties>
    >,
    positionOffset: positionOffsetProp = EMPTY_OBJECT,
    doNotCoverAnchorEl,
    useContentHeight,
    disableVerticalAdjustment,
    clickAwayListenerProps = EMPTY_OBJECT as ClickAwayListenerProps,
    refreshToken,
    ...other
  } = props
  const paneRef = useRef<HTMLDivElement | null>(null)
  const ignoreResizeEventRef = useRef<boolean>(false)
  const paneSize = useSize(paneRef?.current)

  const positionOffset = useMemo(
    () => ({
      ...defaultOffset,
      ...positionOffsetProp
    }),
    [positionOffsetProp]
  )

  const getAnchorRect = useCallback(() => {
    const resolvedAnchorEl = getAnchorEl(anchorEl)

    // If an anchor element wasn't provided, just use the parent body element of this Popover
    const anchorElement =
      resolvedAnchorEl && resolvedAnchorEl.nodeType === 1
        ? resolvedAnchorEl
        : ownerDocument(paneRef.current).body
    return anchorElement.getBoundingClientRect()
  }, [anchorEl])

  // Returns the top/left offset of the position
  // to attach to on the anchor element (or body if none is provided)
  const getAnchorOffset = useCallback(
    (contentAnchorOffset: number) => {
      if (anchorReference === 'anchorPosition') {
        if (process.env.NODE_ENV !== 'production') {
          if (!anchorPosition) {
            console.error(
              'Tui: you need to provide a `anchorPosition` prop when using ' +
                '<Popover anchorReference="anchorPosition" />.'
            )
          }
        }
        return anchorPosition
      }

      const anchorRect = getAnchorRect()

      if (process.env.NODE_ENV !== 'production') {
        const box = anchorRect

        if (
          process.env.NODE_ENV !== 'test' &&
          box.top === 0 &&
          box.left === 0 &&
          box.right === 0 &&
          box.bottom === 0
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            [
              'Tui: the `anchorEl` prop provided to the component is invalid.',
              'The anchor element should be part of the document layout.',
              "Make sure the element is present in the document or that it's not display none."
            ].join('\n')
          )
        }
      }

      const anchorVertical =
        contentAnchorOffset === 0 ? anchorOrigin.vertical : 'center'

      return {
        top: anchorRect.top + getOffsetTop(anchorRect, anchorVertical),
        left:
          anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal)
      }
    },
    [
      anchorOrigin.horizontal,
      anchorOrigin.vertical,
      anchorPosition,
      anchorReference,
      getAnchorRect
    ]
  )

  // Returns the vertical offset of inner content to anchor the transform on if provided
  const getContentAnchorOffset = useCallback(
    (element: Element) => {
      let contentAnchorOffset = 0

      if (getContentAnchorEl && anchorReference === 'anchorEl') {
        const contentAnchorEl = getContentAnchorEl(element) as HTMLElement

        if (contentAnchorEl && element.contains(contentAnchorEl)) {
          const scrollTop = getScrollParent(element, contentAnchorEl)
          contentAnchorOffset =
            contentAnchorEl.offsetTop +
              contentAnchorEl.clientHeight / 2 -
              scrollTop || 0
        }

        // != the default value
        if (process.env.NODE_ENV !== 'production') {
          if (anchorOrigin.vertical !== 'top') {
            console.error(
              [
                'Tui: you can not change the default `anchorOrigin.vertical` value ',
                'when also providing the `getContentAnchorEl` prop to the popover component.',
                'Only use one of the two props.',
                'Set `getContentAnchorEl` to `null | undefined`' +
                  ' or leave `anchorOrigin.vertical` unchanged.'
              ].join('\n')
            )
          }
        }
      }

      return contentAnchorOffset
    },
    [anchorOrigin.vertical, anchorReference, getContentAnchorEl]
  )

  // Return the base transform origin using the element
  // and taking the content anchor offset into account if in use
  const getTransformOrigin = useCallback(
    (elemRect: string | DOMRect, contentAnchorOffset = 0) => {
      return {
        vertical:
          getOffsetTop(elemRect, transformOrigin.vertical) +
          contentAnchorOffset,
        horizontal: getOffsetLeft(elemRect, transformOrigin.horizontal)
      }
    },
    [transformOrigin.horizontal, transformOrigin.vertical]
  )

  const getPositioningStyle = useCallback(
    (element: HTMLElement) => {
      // Check if the parent has requested anchoring on an inner content node
      const contentAnchorOffset = getContentAnchorOffset(element)
      //reset the bottom of the element before computing it below
      element.style.bottom = ''
      const elemRect = {
        width: element.offsetWidth,
        height: element.offsetHeight
      }

      // Get the transform origin point on the element itself
      const elemTransformOrigin = getTransformOrigin(
        elemRect as DOMRect,
        contentAnchorOffset
      )

      if (anchorReference === 'none') {
        return {
          positioning: {
            top: null,
            left: null,
            bottom: null,
            transformOrigin: getTransformOriginValue(elemTransformOrigin)
          }
        }
      }

      // Get the offset of the anchoring element
      const anchorOffset = getAnchorOffset(contentAnchorOffset)

      // Calculate element positioning
      let top =
        (anchorOffset ? anchorOffset.top : 0) - elemTransformOrigin.vertical
      let left =
        (anchorOffset ? anchorOffset.left : 0) - elemTransformOrigin.horizontal
      const bottom = top + elemRect.height
      const right = left + elemRect.width
      // eslint-disable-next-line no-console
      // console.log(
      //   ` ---- popover initial position: ${JSON.stringify(
      //     {
      //       top,
      //       left,
      //       bottom,
      //       right
      //     },
      //     undefined,
      //     2
      //   )}`
      // )

      // Use the parent window of the anchorEl if provided
      const containerWindow = ownerWindow(getAnchorEl(anchorEl))

      // Window thresholds taking required margin into account
      const heightThreshold = containerWindow.innerHeight - marginThreshold
      const widthThreshold = containerWindow.innerWidth - marginThreshold

      // Check if the vertical axis needs shifting
      if (!disableVerticalAdjustment) {
        if (top < marginThreshold) {
          const diff = top - marginThreshold
          top -= diff
        } else if (bottom > heightThreshold) {
          const diff = bottom - heightThreshold
          top -= diff
        }
        // check top margin again in the case we shifted too close to the top
        if (top < marginThreshold) {
          const diff = top - marginThreshold
          top -= diff
        }
      }

      // eslint-disable-next-line no-console
      // console.log(
      //   ` ---- popover after vertical shift position: ${JSON.stringify(
      //     {
      //       top,
      //       left,
      //       bottom,
      //       right
      //     },
      //     undefined,
      //     2
      //   )}`
      // )
      const anchorRect = getAnchorRect()

      if (doNotCoverAnchorEl && top < (anchorOffset?.top ?? 0)) {
        top = (anchorOffset?.top ?? 0) - elemRect.height - anchorRect.height + 1

        // eslint-disable-next-line no-console
        // console.log(` ---- popover doNotCoverAnchorEl adjustment top: ${top}`)
      }

      if (process.env.NODE_ENV !== 'production') {
        if (
          elemRect.height > heightThreshold &&
          elemRect.height &&
          heightThreshold
        ) {
          console.error(
            [
              'Tui: the popover component is too tall.',
              `Some part of it can not be seen on the screen (${
                elemRect.height - heightThreshold
              }px).`,
              'Please consider adding a `max-height` to improve the user-experience.'
            ].join('\n')
          )
        }
      }

      // Check if the horizontal axis needs shifting
      if (left < marginThreshold) {
        const diff = left - marginThreshold
        left -= diff
      } else if (right > widthThreshold) {
        const diff = right - widthThreshold
        left -= diff
      }

      top = Math.round(top + positionOffset.y)
      left = Math.round(left + positionOffset.x)

      const rect = {
        top: top < marginThreshold ? marginThreshold : top,
        left: left < marginThreshold ? marginThreshold : left,
        bottom:
          !useContentHeight && !doNotCoverAnchorEl && bottom >= heightThreshold
            ? marginThreshold
            : elemRect.height + top
      }

      // const rect = {
      //   top: top < marginThreshold ? `${marginThreshold}px` : `${top}px`,
      //   left: left < marginThreshold ? `${marginThreshold}px` : `${left}px`,
      //   bottom:
      //     bottom >= heightThreshold && !doNotCoverAnchorEl
      //       ? `${marginThreshold}px`
      //       : `${elemRect.height}px`
      // }

      const retVal = {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        bottom:
          !useContentHeight && !doNotCoverAnchorEl && bottom >= heightThreshold
            ? `${rect.bottom}px`
            : null,
        transformOrigin: getTransformOriginValue({
          vertical:
            (rect.top + rect.bottom) / 2 <
            (anchorRect.top + anchorRect.bottom) / 2
              ? `${anchorRect.top - rect.top}px`
              : `0px`,
          horizontal: `${left - Math.round(anchorRect.left)}px`
        })
      }

      // eslint-disable-next-line no-console
      // console.log(
      //   ` ---- popover getPositioningStyle retVal: ${JSON.stringify(
      //     retVal,
      //     undefined,
      //     2
      //   )}`
      // )

      return {
        positioning: retVal,
        heightThreshold,
        widthThreshold
      }
    },
    [
      anchorEl,
      anchorReference,
      getAnchorOffset,
      getContentAnchorOffset,
      getTransformOrigin,
      getAnchorRect,
      marginThreshold,
      positionOffset,
      doNotCoverAnchorEl,
      useContentHeight,
      disableVerticalAdjustment
    ]
  )

  const delayExecution = useTimeout(100)

  const setPositioningStyles = useCallback(
    (refreshToken?: boolean | number) => {
      const element = paneRef.current

      if (!element) {
        return
      }

      const { positioning, heightThreshold } = getPositioningStyle(element)
      // eslint-disable-next-line no-console
      // console.log(
      //   `--- popover setPositioningStyles() positioning: ${JSON.stringify(
      //     positioning,
      //     undefined,
      //     2
      //   )}`
      // )

      if (
        (positioning.top !== null && element.style.top !== positioning.top) ||
        (positioning.left !== null &&
          element.style.left !== positioning.left) ||
        (positioning.bottom !== null &&
          positioning.bottom !== `${marginThreshold}px` &&
          element.style.bottom !== positioning.bottom)
      ) {
        // emit event only if position has change
        onReposition && onReposition(refreshToken)
        // provide an extra delay render to ensure the position of the elements
        // are captured correctly by dependent element (i.e. adorner for the dropdown)
        onReposition && delayExecution(onReposition)
      }

      if (positioning.top !== null) {
        element.style.top = positioning.top
      }
      if (positioning.left !== null) {
        element.style.left = positioning.left
      }
      if (positioning.bottom !== null) {
        if (
          disableVerticalAdjustment &&
          positioning.bottom === `${marginThreshold}px` &&
          heightThreshold
        ) {
          // circuit breaker for paneSize resize event when setting height
          ignoreResizeEventRef.current = true
          element.style.height = `${
            heightThreshold - parseInt(positioning.top) - marginThreshold
          }px`
          // reset paneSize resize event listener
          delayExecution(() => (ignoreResizeEventRef.current = false))
        } else {
          element.style.bottom = positioning.bottom
        }
      } else {
        element.style.height = ''
      }

      element.style.transformOrigin = positioning.transformOrigin
    },
    [
      delayExecution,
      disableVerticalAdjustment,
      getPositioningStyle,
      marginThreshold,
      onReposition
    ]
  )

  const {
    onEnter: onEnterProp,
    onEntering: onEnteringProp,
    onEntered: onEnteredProp,
    onExit: onExitProp,
    onExited: onExitedProp,
    onExiting: onExitingProp,
    ...restTransitionProps
  } = TransitionProps

  const handleEnter = useCallback(
    (element: HTMLElement, isAppearing: boolean) => {
      if (onEnter) {
        onEnter(element, isAppearing)
      }
      if (onEnterProp) {
        onEnterProp(element, isAppearing)
      }
    },
    [onEnter, onEnterProp]
  )

  const handleEntering = useCallback(
    (element: HTMLElement, isAppearing: boolean) => {
      if (onEntering) {
        onEntering(element, isAppearing)
      }
      if (onEnteringProp) {
        onEnteringProp(element, isAppearing)
      }

      setPositioningStyles()
    },
    [onEnteringProp, onEntering, setPositioningStyles]
  )

  const handleEntered = useCallback(
    (element: HTMLElement, isAppearing: boolean) => {
      if (onEntered) {
        onEntered(element, isAppearing)
      }

      if (onEnteredProp) {
        onEnteredProp(element, isAppearing)
      }

      setPositioningStyles()
    },
    [onEntered, onEnteredProp, setPositioningStyles]
  )

  const handleExit = useCallback(
    (element: HTMLElement) => {
      if (onExit) {
        onExit(element)
      }
      if (onExitProp) {
        onExitProp(element)
      }
    },
    [onExit, onExitProp]
  )

  const handleExited = useCallback(
    (element: HTMLElement) => {
      if (onExited) {
        onExited(element)
      }
      if (onExitedProp) {
        onExitedProp(element)
      }
    },
    [onExited, onExitedProp]
  )

  const handleExiting = useCallback(
    (element: HTMLElement) => {
      if (onExiting) {
        onExiting(element)
      }
      if (onExitingProp) {
        onExitingProp(element)
      }
    },
    [onExiting, onExitingProp]
  )

  const handlePaperRef = useCallback((instance: HTMLDivElement | null) => {
    paneRef.current = instance
  }, [])

  // Recalculate popover position when it need to adjust it's position in reference to the container viewport
  useEffect(() => {
    if (!ignoreResizeEventRef.current && open && paneSize) {
      setPositioningStyles(refreshToken)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, paneSize, setPositioningStyles, refreshToken])

  useImperativeHandle(
    action,
    () =>
      open
        ? {
            updatePosition: setPositioningStyles
          }
        : null,
    [open, setPositioningStyles]
  )

  useEffect(() => {
    if (!open) {
      return undefined
    }

    const handleResize = debounce(setPositioningStyles)

    window.addEventListener('resize', handleResize)
    return () => {
      handleResize.clear()
      window.removeEventListener('resize', handleResize)
    }
  }, [open, setPositioningStyles])

  let transitionDuration: TransitionPropsType['timeout'] | 'auto' | undefined =
    transitionDurationProp

  if (transitionDurationProp === 'auto' && !TransitionComponent.supportAuto) {
    transitionDuration = undefined
  }

  // If the container prop is provided, use that
  // If the anchorEl prop is provided, use its parent body element as the container
  // If neither are provided let the Modal take care of choosing the container
  const container =
    containerProp ||
    (anchorEl ? ownerDocument(getAnchorEl(anchorEl)).body : undefined)

  const { elevation = 200, border, __css, ...rest } = paneProps

  const pane__css = useMemo(
    () => ({
      position: 'absolute',
      overflowY: 'auto',
      overflowX: 'hidden',
      maxWidth: 'calc(100% - 2rem)',
      maxHeight: 'calc(100% - 2rem)',
      // save guard when modal is use with disableEnforceFocus set to true
      pointerEvents: 'auto',
      // We disable the focus ring for mouse, touch and keyboard users.
      outline: 0,
      ...__css
    }),
    [__css]
  )

  // eslint-disable-next-line no-console
  // console.log(`------ popover render open: ${open} ------`)
  const content = (
    <PanePrivate
      data-csg-test="Popover"
      automation-id="Popover"
      elevation={elevation}
      ref={handlePaperRef}
      styledBorder={border}
      __css={pane__css}
      {...rest}
    >
      {typeof children === 'function' ? (children as Function)() : children}
    </PanePrivate>
  )

  return (
    <Modal
      container={container}
      open={open}
      ref={ref}
      BackdropProps={backdropProps}
      {...other}
    >
      <TransitionComponent
        appear
        in={open}
        onEnter={handleEnter}
        onEntered={handleEntered}
        onEntering={handleEntering}
        onExit={handleExit}
        onExited={handleExited}
        onExiting={handleExiting}
        timeout={transitionDuration as TransitionPropsType['timeout']}
        transitionStyles={transitionStyles}
        {...restTransitionProps}
      >
        {isNotEmptyObject(clickAwayListenerProps) ? (
          <ClickAwayListener {...clickAwayListenerProps}>
            {content}
          </ClickAwayListener>
        ) : (
          content
        )}
      </TransitionComponent>
    </Modal>
  )
})

if (__DEV__) {
  Popover.displayName = 'Popover'
}

export default Popover
